/**
 * Checkout Service - Centralized checkout operations for App-client
 * Provides context-aware checkout functionality using the new DDD backend architecture
 */

import { buildApiEndpoint } from '../utils/apiHelpers';
import apiClient from '../config/api';

/**
 * Checkout context enumeration
 */
export const CheckoutContext = {
  KANBAN: 'kanban',
  APPOINTMENTS_PAGE: 'appointments-page',
  MOBILE_APP: 'mobile-app'
};

/**
 * Checkout Service Class - Main service for all checkout operations
 */
export class CheckoutService {
  constructor() {
    this.client = apiClient;
  }

  /**
   * Create checkout session for given context
   * @param {Array} groupIds - Appointment group IDs
   * @param {string} context - Checkout context
   * @returns {Promise<Object>} Checkout session data
   */
  async createCheckoutSession(groupIds, context = CheckoutContext.MOBILE_APP) {
    // Validate context
    if (!Object.values(CheckoutContext).includes(context)) {
      throw new Error(`Invalid checkout context: ${context}`);
    }
    
    // Validate groupIds
    if (!Array.isArray(groupIds) || groupIds.length === 0) {
      throw new Error('Group IDs must be a non-empty array');
    }
    
    const endpoint = buildApiEndpoint(`appointments/checkout/${context}/session`);
    
    try {
      const response = await this.client.post(endpoint, { group_ids: groupIds });
      return response.data;
    } catch (error) {
      console.error(`Creating ${context} checkout session error:`, error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || `Failed to create ${context} checkout session`;
      throw new Error(errorMessage);
    }
  }

  /**
   * Process payment for given context
   * @param {Object} paymentData - Payment details
   * @param {string} context - Checkout context
   * @returns {Promise<Object>} Payment result
   */
  async processPayment(paymentData, context = CheckoutContext.MOBILE_APP) {
    const endpoint = buildApiEndpoint(`appointments/checkout/${context}`);
    
    try {
      const response = await this.client.post(endpoint, paymentData);
      return response.data;
    } catch (error) {
      console.error(`Processing ${context} payment error:`, error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || `Failed to process ${context} payment`;
      throw new Error(errorMessage);
    }
  }

  /**
   * Get checkout preview for validation
   * @param {Array} groupIds - Appointment group IDs
   * @param {string} context - Checkout context
   * @returns {Promise<Object>} Checkout preview data
   */
  async getCheckoutPreview(groupIds, context = CheckoutContext.MOBILE_APP) {
    // Validate context
    if (!Object.values(CheckoutContext).includes(context)) {
      throw new Error(`Invalid checkout context: ${context}`);
    }
    
    // Validate groupIds
    if (!Array.isArray(groupIds) || groupIds.length === 0) {
      throw new Error('Group IDs must be a non-empty array');
    }
    
    const queryString = groupIds.map(id => `group_ids=${id}`).join('&');
    const endpoint = buildApiEndpoint(`appointments/checkout/preview/${context}?${queryString}`);
    
    try {
      const response = await this.client.get(endpoint);
      return response.data;
    } catch (error) {
      console.error(`Getting ${context} checkout preview error:`, error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || `Failed to get ${context} checkout preview`;
      throw new Error(errorMessage);
    }
  }

  // Context-specific convenience methods

  /**
   * Kanban-specific checkout operations
   */
  async createKanbanCheckoutSession(groupIds) {
    return this.createCheckoutSession(groupIds, CheckoutContext.KANBAN);
  }

  async processKanbanPayment(paymentData) {
    return this.processPayment(paymentData, CheckoutContext.KANBAN);
  }

  async getKanbanCheckoutPreview(groupIds) {
    return this.getCheckoutPreview(groupIds, CheckoutContext.KANBAN);
  }

  /**
   * Appointments page specific checkout operations
   */
  async createAppointmentsPageCheckoutSession(groupIds) {
    return this.createCheckoutSession(groupIds, CheckoutContext.APPOINTMENTS_PAGE);
  }

  async processAppointmentsPagePayment(paymentData) {
    return this.processPayment(paymentData, CheckoutContext.APPOINTMENTS_PAGE);
  }

  async getAppointmentsPageCheckoutPreview(groupIds) {
    return this.getCheckoutPreview(groupIds, CheckoutContext.APPOINTMENTS_PAGE);
  }

  /**
   * Mobile app specific checkout operations
   */
  async createMobileAppCheckoutSession(groupIds) {
    return this.createCheckoutSession(groupIds, CheckoutContext.MOBILE_APP);
  }

  async processMobileAppPayment(paymentData) {
    return this.processPayment(paymentData, CheckoutContext.MOBILE_APP);
  }

  async getMobileAppCheckoutPreview(groupIds) {
    return this.getCheckoutPreview(groupIds, CheckoutContext.MOBILE_APP);
  }
}

/**
 * Default checkout service instance
 */
export const checkoutService = new CheckoutService();

/**
 * Legacy API compatibility - maps to new service methods
 * Maintains backward compatibility while encouraging migration to new service
 */
export const createMergedCheckoutSession = async (groupIds, context = CheckoutContext.MOBILE_APP) => {
  return checkoutService.createCheckoutSession(groupIds, context);
};

export const processAppointmentPayment = async (paymentData, context = CheckoutContext.MOBILE_APP) => {
  return checkoutService.processPayment(paymentData, context);
};

export const getCheckoutPreview = async (groupIds, context = CheckoutContext.MOBILE_APP) => {
  return checkoutService.getCheckoutPreview(groupIds, context);
};